// Form validation utilities

export const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;
  return regex.test(email);
};

export const validatePassword = (password) => {
  // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
  return password.length >= 8;
};

export const validatePasswordStrength = (password) => {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isLongEnough = password.length >= 8;

  return {
    isValid: hasUpperCase && hasLowerCase && hasNumber && isLongEnough,
    strength: [hasUpperCase, hasLowerCase, hasNumber, hasSpecialChar, isLongEnough].filter(Boolean).length,
    feedback: {
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasSpecialChar,
      isLongEnough
    }
  };
};

export const validatePhone = (phone) => {
  // Basic phone validation - digits, spaces, dashes, parentheses, plus
  const regex = /^[\d\s\-\+\(\)]+$/;
  return regex.test(phone) && phone.replace(/\D/g, '').length >= 9;
};

export const validateUrl = (url) => {
  if (!url || !url.toString().trim()) return true;
  const normalizedUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
  try {
    const parsedUrl = new URL(normalizedUrl);
    return !!parsedUrl.hostname && parsedUrl.hostname.includes('.') && parsedUrl.hostname.split('.').pop().length >= 2;
  } catch {
    return false;
  }
};

export const validateRequired = (value) => {
  return value && value.toString().trim().length > 0;
};

export const validateMinLength = (value, minLength) => {
  return value && value.toString().trim().length >= minLength;
};

export const validateMaxLength = (value, maxLength) => {
  return !value || value.toString().trim().length <= maxLength;
};

export const validatePostalCode = (postalCode) => {
  // Flexible postal code validation
  return postalCode && postalCode.trim().length >= 4;
};

// Error messages
export const getErrorMessage = (fieldName, type, params = {}, t = null) => {
  // If translation function is provided, use it for localized messages
  if (t) {
    // Determine if this is a contact or partnership form based on field name
    const fieldLower = fieldName.toLowerCase();
    const isPartnershipForm = fieldLower.includes('company') || 
                              fieldLower.includes('brand') || 
                              fieldLower.includes('cooperation') ||
                              fieldLower.includes('contact');
    const formPrefix = isPartnershipForm ? 'brands.validation' : 'contact.validation';
    
    const messageKeys = {
      required: `${formPrefix}.nameRequired`,
      email: `${formPrefix}.emailInvalid`,
      password: `${formPrefix}.passwordWeak`,
      passwordStrength: `${formPrefix}.passwordStrength`,
      phone: `${formPrefix}.phoneInvalid`,
      url: `${formPrefix}.urlInvalid`,
      minLength: `${formPrefix}.minLength`,
      maxLength: `${formPrefix}.maxLength`,
      postalCode: `${formPrefix}.postalCode`
    };
    
    // Get the key and handle min/max parameters
    let key = messageKeys[type];
    if (!key) return 'Invalid input';
    
    // For field-specific messages, adjust key based on field
    if (type === 'required') {
      if (fieldLower.includes('name') || fieldLower.includes('company') || fieldLower.includes('person')) {
        key = `${formPrefix}.nameRequired`;
      } else if (fieldLower.includes('email')) {
        key = `${formPrefix}.emailRequired`;
      } else if (fieldLower.includes('phone')) {
        key = `${formPrefix}.phoneRequired`;
      } else if (fieldLower.includes('subject')) {
        key = `${formPrefix}.subjectRequired`;
      } else if (fieldLower.includes('message')) {
        key = `${formPrefix}.messageRequired`;
      } else if (fieldLower.includes('description')) {
        key = `${formPrefix}.descriptionRequired`;
      } else if (fieldLower.includes('details')) {
        key = `${formPrefix}.detailsRequired`;
      }
    }
    
    if (type === 'minLength') {
      if (fieldLower.includes('name') || fieldLower.includes('company') || fieldLower.includes('person')) {
        key = `${formPrefix}.nameMinLength`;
      } else if (fieldLower.includes('subject')) {
        key = `${formPrefix}.subjectMinLength`;
      } else if (fieldLower.includes('message')) {
        key = `${formPrefix}.messageMinLength`;
      } else if (fieldLower.includes('description')) {
        key = `${formPrefix}.descriptionMinLength`;
      } else if (fieldLower.includes('details')) {
        key = `${formPrefix}.detailsMinLength`;
      }
    }
    
    return t(key) || 'Invalid input';
  }
  
  // Fallback to English messages if no translation function provided
  const messages = {
    required: `${fieldName} is required`,
    email: 'Please enter a valid email address',
    password: 'Password must be at least 8 characters',
    passwordStrength: 'Password must contain uppercase, lowercase, and number',
    phone: 'Please enter a valid phone number',
    url: 'Please enter a valid website URL',
    minLength: `${fieldName} must be at least ${params.min} characters`,
    maxLength: `${fieldName} must be at most ${params.max} characters`,
    postalCode: 'Please enter a valid postal code'
  };
  
  return messages[type] || 'Invalid input';
};

// Form validation helper
export const validateForm = (formData, rules, t = null) => {
  const errors = {};
  
  Object.keys(rules).forEach(field => {
    const value = formData[field];
    const fieldRules = rules[field];
    
    if (fieldRules.required && !validateRequired(value)) {
      errors[field] = getErrorMessage(fieldRules.label || field, 'required', {}, t);
      return;
    }
    
    if (fieldRules.email && value && !validateEmail(value)) {
      errors[field] = getErrorMessage(field, 'email', {}, t);
      return;
    }
    
    if (fieldRules.password && value && !validatePassword(value)) {
      errors[field] = getErrorMessage(field, 'password', {}, t);
      return;
    }
    
    if (fieldRules.phone && value && !validatePhone(value)) {
      errors[field] = getErrorMessage(field, 'phone', {}, t);
      return;
    }

    if (fieldRules.url && value && !validateUrl(value)) {
      errors[field] = getErrorMessage(field, 'url', {}, t);
      return;
    }
    
    if (fieldRules.minLength && value && !validateMinLength(value, fieldRules.minLength)) {
      errors[field] = getErrorMessage(fieldRules.label || field, 'minLength', { min: fieldRules.minLength }, t);
      return;
    }
    
    if (fieldRules.maxLength && value && !validateMaxLength(value, fieldRules.maxLength)) {
      errors[field] = getErrorMessage(fieldRules.label || field, 'maxLength', { max: fieldRules.maxLength }, t);
      return;
    }
  });
  
  return errors;
};
